import { useState, useEffect } from "react";
import downArrow from "../../../../assets/design-revamp-product/product-icons/product-arrow.svg";
import addIcon from "../../../../assets/design-revamp-product/product-icons/add-icon.svg";

const styles = `
  .variations-row {
    display: flex;
    gap: 16px;
  }

  .remove-button {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    margin-left: 5px;
  }

  .remove-button:hover {
    color: #bb2d3b;
  }

  .relative {
    position: relative;
  }
`;

const VariationSelector = ({
  onAddVariation,
  variations,
  onRemoveVariation,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newVariationName, setNewVariationName] = useState("");

  const handleAdd = () => {
    if (newVariationName.trim()) {
      onAddVariation({
        name: newVariationName,
        values: [],
      });
      setNewVariationName("");
      setIsOpen(false);
    }
  };

  return (
    <div>
      <div className="product-dropdown-top relative">
        <div className="variation-header" onClick={() => setIsOpen(!isOpen)}>
          <h3
            style={{
              fontSize: "14px",
              width: "85%",
              whiteSpace: "nowrap",
              marginRight: "10px",
            }}
          >
            Variation
          </h3>
          <img
            src={downArrow}
            width={15}
            height={15}
            alt="Down Arrow Dropdown"
          />
        </div>
      </div>

      {isOpen && (
        <>
          <div className="product-dropdown-body">
            <div>
              {variations.map((item, index) => (
                <div
                  key={typeof item === "object" ? item.id : index}
                  className="product-dropdown-items"
                >
                  <span>{typeof item === "object" ? item.name : item}</span>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveVariation(index);
                    }}
                    className="remove-button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="product-dropdown-add-item relative">
              <input
                type="text"
                value={newVariationName}
                onChange={(e) => setNewVariationName(e.target.value)}
                placeholder="Add: 'System'"
                className="variation-input"
              />
              <img
                src={addIcon}
                alt="Add Product Variant Icon"
                width={18}
                height={18}
                style={{ position: "absolute", right: "5px" }}
                onClick={handleAdd}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const VariationValues = ({ variation, onUpdateValues }) => {
  const [newValue, setNewValue] = useState("");
  const [openDropdownValue, setOpenDropdownValue] = useState(false);

  const addValue = () => {
    if (newValue.trim()) {
      onUpdateValues({
        ...variation,
        values: [...variation.values, newValue],
      });
      setNewValue("");
    }
  };

  const removeValue = (indexToRemove) => {
    onUpdateValues({
      ...variation,
      values: variation.values.filter((_, index) => index !== indexToRemove),
    });
  };

  return (
    <div className="variation-box">
      <div
        className="product-dropdown-top relative"
        onClick={() => setOpenDropdownValue(!openDropdownValue)}
      >
        <div className="variation-header">
          <h3
            style={{
              fontSize: "14px",
              width: "85%",
              whiteSpace: "nowrap",
              marginRight: "10px",
            }}
          >
            {variation.name}
          </h3>
          <img
            src={downArrow}
            width={15}
            height={15}
            alt="Down Arrow Dropdown"
          />
        </div>
      </div>

      {openDropdownValue && (
        <>
          <div className="product-dropdown-body">
            {variation.values.map((value, index) => (
              <div
                key={typeof value === "object" ? value.id : index}
                className="product-dropdown-items"
              >
                <span>{typeof value === "object" ? value.value : value}</span>
                <button
                  onClick={() => removeValue(index)}
                  className="remove-button"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="product-dropdown-add-item relative">
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    addValue();
                  }
                }}
                placeholder="Add value..."
                className="variation-input"
                style={{ marginBottom: 0 }}
              />
              <img
                src={addIcon}
                alt="Add Product Variant Icon"
                width={18}
                height={18}
                style={{ position: "absolute", right: "5px" }}
                onClick={addValue}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to deduplicate variations by name
const deduplicateVariations = (variations) => {
  if (!Array.isArray(variations)) return [];

  const uniqueVariations = [];
  const seenNames = new Set();

  variations.forEach((variation) => {
    const name = typeof variation === "object" ? variation.name : variation;
    if (!seenNames.has(name)) {
      seenNames.add(name);
      uniqueVariations.push(variation);
    }
  });

  return uniqueVariations;
};

const VariationSystem = ({ onVariationsChange, chosenSubgroup }) => {
  const [variations, setVariations] = useState(
    Object.keys(chosenSubgroup)?.length > 0
      ? deduplicateVariations(chosenSubgroup?.variation_categories)
      : [
          { name: "Type", values: [] },
          { name: "Size", values: [] },
          { name: "Colour", values: [] },
          { name: "System", values: [] },
          { name: "Surface", values: [] },
        ]
  );

  useEffect(() => {
    if (chosenSubgroup) {
      if (chosenSubgroup.variation_categories?.length > 0) {
        // Deduplicate variations to avoid showing duplicates
        const deduplicatedVariations = deduplicateVariations(
          chosenSubgroup.variation_categories
        );
        setVariations(deduplicatedVariations);
      } else {
        setVariations([
          { name: "Type", values: [] },
          { name: "Size", values: [] },
          { name: "Colour", values: [] },
          { name: "System", values: [] },
          { name: "Surface", values: [] },
        ]);
      }
    }
  }, [chosenSubgroup]);

  useEffect(() => {
    const activeVariations = variations.map((variation) => ({
      name: variation.name,
      values: variation.values.map((value) =>
        typeof value === "object" ? value.value : value
      ),
    }));

    onVariationsChange(activeVariations);
  }, [variations, onVariationsChange]);

  const handleAddVariation = (newVariation) => {
    setVariations([...variations, newVariation]);
  };

  const handleRemoveVariation = (indexToRemove) => {
    const newVariations = variations.filter(
      (_, index) => index !== indexToRemove
    );
    setVariations(newVariations);
  };

  const handleUpdateValues = (updatedVariation, index) => {
    const newVariations = [...variations];
    newVariations[index] = updatedVariation;
    setVariations(newVariations);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="variation-container">
        <div className="variations-row">
          <div>
            <VariationSelector
              onAddVariation={handleAddVariation}
              onRemoveVariation={handleRemoveVariation}
              variations={variations}
            />
          </div>
          {variations.map((variation, index) => (
            <div key={variation.id || index}>
              <VariationValues
                variation={variation}
                onUpdateValues={(updated) => handleUpdateValues(updated, index)}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default VariationSystem;
